import { useEffect, useRef, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { consumer } from "@/lib/cable"
import { useAuthStore } from "@/stores/auth-store"
import type { Notification } from "@/lib/types"

interface NotificationMessage {
  type: "notification"
  notification: Notification
}

interface UnreadCountMessage {
  type: "unread_count"
  count: number
}

type CableMessage = NotificationMessage | UnreadCountMessage

/**
 * Subscribes to the NotificationChannel via ActionCable.
 * Automatically invalidates TanStack Query caches when new
 * notifications arrive, so the UI updates in real-time.
 */
export function useNotificationSocket() {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const subscriptionRef = useRef<ReturnType<typeof consumer.subscriptions.create> | null>(null)

  const handleReceived = useCallback(
    (data: CableMessage) => {
      if (data.type === "notification") {
        // Invalidate notifications list so it re-fetches
        queryClient.invalidateQueries({ queryKey: ["notifications"] })
        // Optimistically set unread count from cache if available
        queryClient.invalidateQueries({ queryKey: ["notifications", "unread_count"] })
      } else if (data.type === "unread_count") {
        // Directly update the unread count cache
        queryClient.setQueryData(["notifications", "unread_count"], { count: data.count })
      }
    },
    [queryClient]
  )

  useEffect(() => {
    if (!isAuthenticated) return

    subscriptionRef.current = consumer.subscriptions.create(
      { channel: "NotificationChannel" },
      {
        received(data: unknown) {
          handleReceived(data as CableMessage)
        },
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [isAuthenticated, handleReceived])
}
